import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { StatusBadge } from '@/components/shared/StatusBadge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Search, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { agentAPI } from '@/services/api';
import { toast } from 'sonner';

export default function AgentClients() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  // Compute overall status based on LLC and Tax filings
  const computeOverallStatus = (client) => {
    const llcStatus = client.llc_status || '';
    const taxStatus = client.filing_status || '';
    
    // Remove pending statuses - don't display them
    const statuses = [llcStatus, taxStatus].filter(s => s && s.toUpperCase() !== 'PENDING');
    
    if (statuses.length === 0) return 'DRAFT';
    
    // Priority 1: If any filing is NEED_INFO
    if (statuses.some(s => s.toUpperCase() === 'NEED_INFO')) {
      return 'NEED_INFO';
    }
    
    // Priority 2: If any filing is READY_FOR_REVIEW
    if (statuses.some(s => s.toUpperCase() === 'READY_FOR_REVIEW')) {
      return 'READY_FOR_REVIEW';
    }
    
    // Priority 3: If any filing is SUBMITTED_TO_IRS
    if (statuses.some(s => s.toUpperCase() === 'SUBMITTED_TO_IRS')) {
      return 'SUBMITTED_TO_IRS';
    }
    
    // Priority 4: If all are SUBMITTED
    if (statuses.every(s => s.toUpperCase() === 'SUBMITTED')) {
      return 'SUBMITTED';
    }
    
    // Default: return the first non-empty status
    return statuses[0];
  };

  useEffect(() => {
    const fetchClients = async () => {
      try {
        setLoading(true);
        const data = await agentAPI.getAgentClients();
        setClients(data);
      } catch (error) {
        console.error('Error fetching clients:', error);
        toast.error('Failed to load clients');
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchClients();
    }
  }, [user]);

  const filteredClients = clients.filter(client =>
    client.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!user) {
    return <div>Loading...</div>;
  }

  if (loading) {
    return (
      <DashboardLayout role={user.role} title="My Clients" user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  const handleRowDoubleClick = (clientId) => {
    navigate(`/agent/clients/${clientId}`);
  };

  return (
    <DashboardLayout role={user.role} title="My Clients" user={user}>
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-foreground">My Clients</h2>
          <p className="mt-1 text-muted-foreground">Manage your assigned clients</p>
        </div>

        {/* Search */}
        <Card className="card-elevated">
          <CardContent className="pt-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search clients by name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </CardContent>
        </Card>

        {/* Clients Table */}
        <Card className="card-elevated">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>All Clients ({filteredClients.length})</CardTitle>
              <p className="text-xs text-muted-foreground">Double-click a row to view details</p>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Filings</TableHead>
                  <TableHead>Last Contact</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredClients.map((client) => (
                  <TableRow 
                    key={client.id}
                    onDoubleClick={() => handleRowDoubleClick(client.id)}
                    className="cursor-pointer hover:bg-muted/50"
                  >
                    <TableCell className="font-medium">{client.name}</TableCell>
                    <TableCell>
                      <StatusBadge status={computeOverallStatus(client)} />
                    </TableCell>
                    <TableCell>{client.tax_year || '-'}</TableCell>
                    <TableCell>
                      {client.last_updated ? new Date(client.last_updated).toLocaleDateString() : '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/agent/clients/${client.id}`)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
